import { useEffect, useState } from "react";
import api from "../lib/typedApi";
import { PageHeader, NBCard, NBInput, NBSelect, NBButton, NBTextarea, EmptyState, Avatar } from "../components/UI";
import { toast } from "sonner";
import { Plus, Trash2, X, Check, BookmarkPlus, Sparkles, Zap } from "lucide-react";

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ title: "", description: "", due_date: "", assigned_to: "", recurrence: "none", priority: "medium" });

  // Templates
  const [templates, setTemplates] = useState([]);
  const [tplOpen, setTplOpen] = useState(false);
  const [tplForm, setTplForm] = useState({ title: "", description: "", default_priority: "medium", default_recurrence: "none", icon: "📝" });

  const load = async () => {
    const [t, m, tpl] = await Promise.all([
      api.get("/api/tasks"),
      api.get("/api/members"),
      api.get("/api/task-templates"),
    ]);
    setTasks(t.data);
    setMembers(m.data);
    setTemplates(tpl.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/tasks", form);
      if (form.priority === "urgent") {
        toast.success("Urgent task added — alert emailed to family!");
      } else {
        toast.success("Task added!");
      }
      setOpen(false);
      setForm({ title: "", description: "", due_date: "", assigned_to: "", recurrence: "none", priority: "medium" });
      load();
    } catch { toast.error("Failed"); }
  };

  const saveAsTemplate = async () => {
    if (!form.title.trim()) {
      toast.error("Add a title first to save as template");
      return;
    }
    try {
      await api.post("/api/task-templates", {
        title: form.title,
        description: form.description,
        default_priority: form.priority,
        default_recurrence: form.recurrence,
        default_assigned_to: form.assigned_to || null,
        icon: "📝",
      });
      toast.success("Saved as template!");
      load();
    } catch {
      toast.error("Failed to save template");
    }
  };

  const submitTemplate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/task-templates", tplForm);
      toast.success("Template saved!");
      setTplOpen(false);
      setTplForm({ title: "", description: "", default_priority: "medium", default_recurrence: "none", icon: "📝" });
      load();
    } catch {
      toast.error("Failed");
    }
  };

  const applyTemplate = async (tpl) => {
    try {
      await api.post(`/api/task-templates/${tpl.id}/apply`, {});
      toast.success(`Created "${tpl.title}" from template`);
      load();
    } catch {
      toast.error("Failed to apply template");
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await api.delete(`/api/task-templates/${id}`);
    toast.success("Deleted");
    load();
  };

  const toggle = async (t) => {
    await api.patch(`/api/tasks/${t.id}`, { completed: !t.completed });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/api/tasks/${id}`);
    load();
  };

  const memberById = (id) => members.find((m) => m.id === id);
  const filtered = tasks.filter((t) => filter === "all" ? true : filter === "open" ? !t.completed : t.completed);

  const priorityColors = { urgent: "#E9B5A0", high: "#E8B96B", medium: "#B8CFA9", low: "#A8BFD1" };

  return (
    <div data-testid="tasks-page">
      <PageHeader
        title="Tasks"
        subtitle="Daily and weekly tasks for the family."
        color="#A8BFD1"
        action={
          <div className="flex gap-2 flex-wrap">
            <NBButton testId="new-template-btn" onClick={() => setTplOpen(true)}>
              <BookmarkPlus strokeWidth={2.4} className="w-4 h-4 inline mr-1" />Template
            </NBButton>
            <NBButton testId="add-task-btn" color="#A8BFD1" onClick={() => setOpen(true)}>
              <Plus strokeWidth={2.4} className="w-4 h-4 inline mr-1" />Task
            </NBButton>
          </div>
        }
      />

      {templates.length > 0 && (
        <div className="mb-8" data-testid="templates-strip">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles strokeWidth={2.2} className="w-4 h-4" style={{ color: "var(--hh-terracotta)" }} />
            <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: "var(--hh-ink-soft)" }}>Templates · tap to apply</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {templates.map((tpl) => (
              <div key={tpl.id} className="group flex items-center gap-2 pr-1.5 rounded-full transition-shadow"
                   style={{ background: "var(--hh-card)", border: "1px solid var(--hh-line)", boxShadow: "0 6px 14px -10px rgba(42,31,23,0.20)" }}
                   data-testid={`template-${tpl.id}`}>
                <button
                  onClick={() => applyTemplate(tpl)}
                  className="flex items-center gap-2 pl-3 pr-2 py-2 text-sm font-semibold"
                  data-testid={`apply-template-${tpl.id}`}
                  title={`Create a task from "${tpl.title}"`}
                >
                  <span className="text-base">{tpl.icon || "📝"}</span>
                  <span>{tpl.title}</span>
                  <Zap strokeWidth={2.4} className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100" />
                </button>
                <button
                  onClick={() => deleteTemplate(tpl.id)}
                  className="opacity-30 hover:opacity-100 p-1.5 rounded-full transition-opacity"
                  data-testid={`delete-template-${tpl.id}`}
                  title="Delete template"
                >
                  <Trash2 strokeWidth={2.4} className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-6">
        {["all", "open", "done"].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
            className={`nb-btn px-4 py-2 text-sm capitalize ${filter === f ? "bg-[#A8BFD1]" : "bg-white"}`}>
            {f}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="No tasks here" subtitle="Add tasks to keep track." />
      ) : (
        <div className="space-y-3">
          {filtered.map((t) => {
            const member = memberById(t.assigned_to);
            return (
              <NBCard key={t.id} className="!p-4" data-testid={`task-${t.id}`}>
                <div className="flex items-start gap-3">
                  <button onClick={() => toggle(t)} data-testid={`toggle-task-${t.id}`}
                    className={`w-7 h-7 rounded-md border-2 border-black flex items-center justify-center shrink-0 ${t.completed ? "bg-[#B8CFA9]" : "bg-white"}`}>
                    {t.completed && <Check strokeWidth={3} className="w-4 h-4" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className={`font-bold ${t.completed ? "line-through text-neutral-500" : ""}`}>{t.title}</p>
                    {t.description && <p className="text-sm text-neutral-600">{t.description}</p>}
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="nb-badge" style={{ backgroundColor: priorityColors[t.priority] || "#E8B96B" }}>{t.priority}</span>
                      {t.due_date && <span className="nb-badge bg-white">Due {t.due_date}</span>}
                      {t.recurrence !== "none" && <span className="nb-badge bg-[#B8CFA9]">{t.recurrence}</span>}
                      {member && (
                        <span className="flex items-center gap-1.5">
                          <Avatar name={member.name} color={member.avatar_color} size={20} />
                          <span className="text-xs font-bold">{member.name}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => remove(t.id)} className="nb-btn bg-[#E9B5A0] p-1.5" data-testid={`delete-task-${t.id}`}>
                    <Trash2 strokeWidth={2.5} className="w-4 h-4" />
                  </button>
                </div>
              </NBCard>
            );
          })}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="nb-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "8px 8px 0 0 rgba(0,0,0,1)" }}>
            <div className="flex justify-between mb-4">
              <h2 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>New Task</h2>
              <button onClick={() => setOpen(false)} className="nb-btn bg-[#E9B5A0] p-1.5"><X strokeWidth={3} className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <NBInput testId="task-title" required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <NBTextarea testId="task-desc" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <NBInput testId="task-due" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              <NBSelect testId="task-assigned" value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}>
                <option value="">Unassigned</option>
                {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </NBSelect>
              <NBSelect testId="task-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                <option value="low">Low priority</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">⚠ Urgent (alerts family)</option>
              </NBSelect>
              <NBSelect testId="task-recurrence" value={form.recurrence} onChange={(e) => setForm({ ...form, recurrence: e.target.value })}>
                <option value="none">One-time</option><option value="daily">Daily</option><option value="weekly">Weekly</option>
              </NBSelect>
              <div className="flex gap-2">
                <NBButton testId="submit-task" type="submit" color="#A8BFD1" className="flex-1">Add Task</NBButton>
                <NBButton testId="save-as-template" type="button" onClick={saveAsTemplate} title="Save these settings as a reusable template">
                  <BookmarkPlus strokeWidth={2.4} className="w-4 h-4" />
                </NBButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {tplOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setTplOpen(false)} data-testid="template-modal">
          <div className="nb-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between mb-4">
              <h2 className="text-2xl font-extrabold" style={{ fontFamily: "var(--hh-display)" }}>New template</h2>
              <button onClick={() => setTplOpen(false)} className="nb-btn p-1.5" data-testid="close-template-modal"><X strokeWidth={2.4} className="w-4 h-4" /></button>
            </div>
            <p className="text-sm mb-4" style={{ color: "var(--hh-ink-soft)" }}>
              Save a recurring task pattern (e.g. "Brush teeth", "Pack lunch") so anyone can spawn a fresh one with a tap.
            </p>
            <form onSubmit={submitTemplate} className="space-y-3">
              <div className="flex gap-3">
                <NBInput testId="template-icon" maxLength={4} className="!w-20 text-center text-2xl" placeholder="📝"
                  value={tplForm.icon} onChange={(e) => setTplForm({ ...tplForm, icon: e.target.value })} />
                <NBInput testId="template-title" required placeholder="Template name (e.g. Pack lunch)"
                  value={tplForm.title} onChange={(e) => setTplForm({ ...tplForm, title: e.target.value })} className="flex-1" />
              </div>
              <NBTextarea testId="template-desc" placeholder="Description / steps"
                value={tplForm.description} onChange={(e) => setTplForm({ ...tplForm, description: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <NBSelect testId="template-priority" value={tplForm.default_priority} onChange={(e) => setTplForm({ ...tplForm, default_priority: e.target.value })}>
                  <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                </NBSelect>
                <NBSelect testId="template-recurrence" value={tplForm.default_recurrence} onChange={(e) => setTplForm({ ...tplForm, default_recurrence: e.target.value })}>
                  <option value="none">One-time</option><option value="daily">Daily</option><option value="weekly">Weekly</option>
                </NBSelect>
              </div>
              <NBButton testId="submit-template" type="submit" color="#A8BFD1" className="w-full">Save template</NBButton>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
